import {
  PLAYER_SPEED, AI_SPEED, BALL_RADIUS, PLAYER_RADIUS,
  SHOOT_POWER, PASS_POWER, THROUGH_PASS_POWER,
  BALL_FRICTION, GRAVITY, FRICTION, SPRINT_MULTIPLIER, PLAYER_ACCEL, PLAYER_DECEL,
  dist, normalize, clamp, randomRange, GAME_PHASE, HALF_DURATION, GOAL_DEPTH,
  FIELD_MARGIN, MAX_SHOOT_POWER, MIN_SHOOT_POWER
} from './constants';

// Başlangıç oyun durumu oluştur
export function createInitialState(canvasW, canvasH, role, homeTeamData, awayTeamData) {
  const goalH = canvasH * 0.3;
  const goalY = (canvasH - goalH) / 2;

  const homeTeam = createTeamFromData(homeTeamData, 'home', canvasW, canvasH);
  const awayTeam = createTeamFromData(awayTeamData, 'away', canvasW, canvasH);

  let controlledPlayerIndex;
  if (role === 'player') {
    controlledPlayerIndex = 5;
  } else {
    controlledPlayerIndex = 0;
  }

  return {
    ball: { x: canvasW / 2, y: canvasH / 2, z: 0, vx: 0, vy: 0, vz: 0, spin: 0, lastTouchedBy: null },
    homeTeam,
    awayTeam,
    homeTeamData,
    awayTeamData,
    controlledTeam: 'home',
    controlledPlayerIndex,
    controlledPlayerIndex2: 5, // 2. oyuncu (away) için
    twoPlayer: false,
    role,
    homeScore: 0,
    awayScore: 0,
    phase: GAME_PHASE.KICKOFF,
    half: 1,
    timer: HALF_DURATION,
    goalH,
    goalY,
    goalDepth: GOAL_DEPTH,
    message: '1. DEVRE',
    messageTimer: 90,
    messageType: 'info',
    canvasW,
    canvasH,
    joystick: { active: false, dx: 0, dy: 0 },
    lastGoalTeam: null,
    kickoffTeam: 'home',
    deadBallTimer: 0,
    deadBallPos: null,
    deadBallType: null,
    celebrationTimer: 0,
    goalScorer: null,
    events: [],
    autoSwitch: true,
    switchCooldown: 0,
    cameraShake: 0,
    skillFxTimer: 0,
    skillFx: null,
    // Ölü top sonrası koruma süresi (taç döngüsü önleme)
    outOfBoundsGrace: 0,
    // FIFA tarzı momentum
    momentum: { home: 50, away: 50 },
    possession: { home: 0, away: 0, total: 0 },
    // Power bar (şut gücü)
    shootPower: 0,
    isChargingShoot: false,
    // Kart sistemi
    cards: [], // { player, team, type: 'yellow'|'red', minute }
    // Maç istatistikleri
    stats: {
      home: { shots: 0, shotsOnTarget: 0, fouls: 0, corners: 0, passes: 0, tackles: 0 },
      away: { shots: 0, shotsOnTarget: 0, fouls: 0, corners: 0, passes: 0, tackles: 0 },
    },
    // Sprint bilgisi (trail efekti için)
    isSprinting: false,
  };
}

function createTeamFromData(teamData, side, w, h) {
  const isHome = side === 'home';
  const gkX = isHome ? 30 : w - 30;

  const positionMap = {
    0: { x: gkX, y: h / 2 },
    1: { x: isHome ? w * 0.16 : w * 0.84, y: h * 0.12 },
    2: { x: isHome ? w * 0.14 : w * 0.86, y: h * 0.34 },
    3: { x: isHome ? w * 0.14 : w * 0.86, y: h * 0.66 },
    4: { x: isHome ? w * 0.16 : w * 0.84, y: h * 0.88 },
    5: { x: isHome ? w * 0.30 : w * 0.70, y: h * 0.18 },
    6: { x: isHome ? w * 0.33 : w * 0.67, y: h * 0.40 },
    7: { x: isHome ? w * 0.33 : w * 0.67, y: h * 0.60 },
    8: { x: isHome ? w * 0.30 : w * 0.70, y: h * 0.82 },
    9: { x: isHome ? w * 0.52 : w * 0.48, y: h * 0.34 },
    10: { x: isHome ? w * 0.52 : w * 0.48, y: h * 0.66 },
  };

  return teamData.players.map((p, i) => {
    const pos = positionMap[i] || { x: w / 2, y: h / 2 };
    return {
      x: pos.x,
      y: pos.y,
      vx: 0,
      vy: 0,
      homeX: pos.x,
      homeY: pos.y,
      teamColor: teamData.colors.primary,
      secondaryColor: teamData.colors.secondary,
      side,
      isGoalkeeper: i === 0,
      number: p.number,
      name: p.name,
      rating: p.rating,
      position: p.position,
      hasBall: false,
      kickCooldown: 0,
      stamina: 100,
      fatigue: 0,
      yellowCards: 0,
      redCard: false,
      foulCount: 0,
      skillCooldown: 0,
    };
  });
}

// Oyuncu değiştirme: topa en yakın oyuncuya geç
export function switchToNearestPlayer(state) {
  if (state.switchCooldown > 0) return;
  const team = state.controlledTeam === 'home' ? state.homeTeam : state.awayTeam;
  const ball = state.ball;
  let bestIdx = state.controlledPlayerIndex;
  let bestDist = Infinity;

  for (let i = 0; i < team.length; i++) {
    if (i === state.controlledPlayerIndex) continue;
    if (team[i].isGoalkeeper && state.role === 'player') continue;
    const d = dist(team[i].x, team[i].y, ball.x, ball.y);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  state.controlledPlayerIndex = bestIdx;
  state.switchCooldown = 15;
}

// Otomatik oyuncu değiştirme (top rakip takıma geçince)
function autoSwitchPlayer(state) {
  if (!state.autoSwitch) return;
  if (state.switchCooldown > 0) return;
  if (state.role !== 'player') return;

  const team = state.controlledTeam === 'home' ? state.homeTeam : state.awayTeam;
  const ball = state.ball;
  const current = team[state.controlledPlayerIndex];
  const currentDist = dist(current.x, current.y, ball.x, ball.y);

  // Eğer kontrol edilen oyuncu toptan çok uzaksa en yakın oyuncuya geç
  if (currentDist > 185) {
    let bestIdx = state.controlledPlayerIndex;
    let bestDist = currentDist;
    for (let i = 0; i < team.length; i++) {
      if (team[i].isGoalkeeper) continue;
      const d = dist(team[i].x, team[i].y, ball.x, ball.y);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    if (bestIdx !== state.controlledPlayerIndex) {
      state.controlledPlayerIndex = bestIdx;
      state.switchCooldown = 30;
    }
  }
}

// Ana güncelleme döngüsü
export function updateGame(state, input, input2 = null) {
  const { ball, homeTeam, awayTeam, canvasW, canvasH } = state;

  // Switch cooldown
  if (state.switchCooldown > 0) state.switchCooldown--;
  if (state.cameraShake > 0) state.cameraShake--;

  // Oyuncu değiştirme isteği
  if (input.switchPlayer) {
    switchToNearestPlayer(state);
    input.switchPlayer = false;
  }

  // Mesaj sayacı
  if (state.messageTimer > 0) {
    state.messageTimer--;
    if (state.messageTimer === 0) {
      state.message = null;
      state.messageType = null;
    }
  }

  // Gol kutlaması
  if (state.phase === GAME_PHASE.CELEBRATION) {
    state.celebrationTimer--;
    if (state.celebrationTimer <= 0) {
      resetPositions(state);
      state.phase = GAME_PHASE.KICKOFF;
      state.kickoffTeam = state.lastGoalTeam === 'home' ? 'away' : 'home';
      state.message = 'BAŞLA!';
      state.messageTimer = 60;
      state.messageType = 'info';
    }
    return state;
  }

  // Devre arası
  if (state.phase === GAME_PHASE.HALF_TIME) {
    state.messageTimer--;
    if (state.messageTimer <= 0) {
      state.half = 2;
      state.timer = HALF_DURATION;
      switchSides(state);
      resetPositions(state);
      state.phase = GAME_PHASE.KICKOFF;
      state.kickoffTeam = 'away';
      state.message = '2. DEVRE';
      state.messageTimer = 90;
      state.messageType = 'info';
    }
    return state;
  }

  // Maç bitti
  if (state.phase === GAME_PHASE.FULL_TIME) return state;

  // Kickoff bekleme
  if (state.phase === GAME_PHASE.KICKOFF || state.phase === GAME_PHASE.SECOND_HALF_KICKOFF) {
    if (state.messageTimer <= 0) {
      state.phase = GAME_PHASE.PLAYING;
    }
    return state;
  }

  // Ölü top durakları (taç, korner, kale vuruşu)
  if (state.deadBallTimer > 0) {
    state.deadBallTimer--;
    if (state.deadBallTimer <= 0) {
      state.phase = GAME_PHASE.PLAYING;
      // Taç/korner sonrası tekrar çıkmasını engelle
      state.outOfBoundsGrace = 60;

      // Taç atışında topu sahaya doğru hafifçe fırlat
      if (state.deadBallType === 'throw_in') {
        const centerY = canvasH / 2;
        ball.vy = ball.y < centerY ? 3 : -3; // sahaya doğru
        ball.vx = (state.deadBallOwner === 'home' ? 2 : -2);
      }
      // Korner'de topu penaltı sahasına doğru fırlat
      if (state.deadBallType === 'corner') {
        const goalX = ball.x < canvasW / 2 ? canvasW * 0.3 : canvasW * 0.7;
        const dir = normalize(goalX - ball.x, canvasH / 2 - ball.y);
        ball.vx = dir.x * 6;
        ball.vy = dir.y * 6;
        ball.vz = 4;
      }
      // Kale vuruşunda topu orta sahaya doğru gönder
      if (state.deadBallType === 'goal_kick') {
        ball.vx = (ball.x < canvasW / 2 ? 5 : -5);
        ball.vy = randomRange(-2, 2);
        ball.vz = 3;
      }
      state.deadBallType = null;
      state.deadBallOwner = null;
    }
    return state;
  }

  // Süre
  state.timer -= 1 / 60;
  if (state.timer <= 0) {
    state.timer = 0;
    if (state.half === 1) {
      state.phase = GAME_PHASE.HALF_TIME;
      state.message = 'DEVRE ARASI';
      state.messageTimer = 150;
      state.messageType = 'halftime';
      ball.vx = 0; ball.vy = 0; ball.vz = 0;
      return state;
    } else {
      state.phase = GAME_PHASE.FULL_TIME;
      state.message = state.homeScore > state.awayScore
        ? `${state.homeTeamData.name} KAZANDI!`
        : state.awayScore > state.homeScore
          ? `${state.awayTeamData.name} KAZANDI!`
          : 'BERABERE!';
      state.messageTimer = 300;
      state.messageType = 'fulltime';
      ball.vx = 0; ball.vy = 0; ball.vz = 0;
      return state;
    }
  }

  // Kontrol edilen oyuncuyu hareket ettir (1. oyuncu - home)
  const player = homeTeam[state.controlledPlayerIndex];

  if (input.joystick.active) {
    const speed = player.isGoalkeeper ? PLAYER_SPEED * 0.8 : PLAYER_SPEED;
    const sprintMult = input.sprint ? SPRINT_MULTIPLIER : 1;
    const staminaMult = player.stamina < 20 ? 0.7 : 1;
    const targetVx = input.joystick.dx * speed * sprintMult * staminaMult;
    const targetVy = input.joystick.dy * speed * sprintMult * staminaMult;
    player.vx += (targetVx - player.vx) * PLAYER_ACCEL;
    player.vy += (targetVy - player.vy) * PLAYER_ACCEL;
    if (input.sprint && player.stamina > 0) {
      player.stamina = Math.max(0, player.stamina - 0.15);
      player.fatigue += 0.01;
    }
  } else {
    player.vx *= (1 - PLAYER_DECEL);
    player.vy *= (1 - PLAYER_DECEL);
  }

  // Sprint durumu kaydet
  state.isSprinting = input.sprint && input.joystick.active;

  // Power bar güncelleme (şut gücü şarjı) - 1. oyuncu
  if (input.chargingShoot) {
    state.isChargingShoot = true;
    state.shootPower = Math.min(1, state.shootPower + 0.02);
  } else if (state.isChargingShoot) {
    state.isChargingShoot = false;
    const d = dist(ball.x, ball.y, player.x, player.y);
    if (d < PLAYER_RADIUS + BALL_RADIUS + 15 && player.kickCooldown <= 0) {
      const isPowerShot = Boolean(input.sprint);
      const shotPowerBase = MIN_SHOOT_POWER + (MAX_SHOOT_POWER - MIN_SHOOT_POWER) * state.shootPower;
      const power = shotPowerBase * (isPowerShot ? 1.14 : 0.95);
      const ratingBonus = player.rating / 500;
      const targetX = state.canvasW;
      const spread = isPowerShot ? 58 : 30;
      const targetY = state.canvasH / 2 + randomRange(-spread, spread) * (1 - state.shootPower * 0.25);
      const dir = normalize(targetX - ball.x, targetY - ball.y);
      ball.vx = dir.x * power * (0.8 + ratingBonus);
      ball.vy = dir.y * power * (0.8 + ratingBonus);
      ball.vz = randomRange(isPowerShot ? 2.5 : 1.6, isPowerShot ? 5.5 : 3.8) * state.shootPower;
      ball.spin = isPowerShot
        ? randomRange(-0.45, 0.45)
        : randomRange(-1.4, 1.4) * (0.8 + player.rating / 220);
      ball.lastTouchedBy = player.side;
      applyCameraShake(state, (isPowerShot ? 11 : 7) + Math.floor(state.shootPower * 6));
      player.kickCooldown = 20;
      state.stats.home.shots++;
      const goalY = state.canvasH / 2;
      if (Math.abs(targetY - goalY) < state.goalH / 2) state.stats.home.shotsOnTarget++;
    }
    state.shootPower = 0;
  }

  // Stamina regen 1. oyuncu
  if (!input.sprint) {
    const regenRate = 0.05 - player.fatigue * 0.005;
    player.stamina = Math.min(100, player.stamina + Math.max(0.01, regenRate));
  }

  // 2. oyuncu hareketi (away team) - sadece twoPlayer modunda
  if (state.twoPlayer && input2) {
    const player2 = awayTeam[state.controlledPlayerIndex2];
    if (player2) {
      if (input2.joystick.active) {
        const speed = player2.isGoalkeeper ? PLAYER_SPEED * 0.8 : PLAYER_SPEED;
        const sprintMult = input2.sprint ? SPRINT_MULTIPLIER : 1;
        const staminaMult = player2.stamina < 20 ? 0.7 : 1;
        const targetVx2 = input2.joystick.dx * speed * sprintMult * staminaMult;
        const targetVy2 = input2.joystick.dy * speed * sprintMult * staminaMult;
        player2.vx += (targetVx2 - player2.vx) * PLAYER_ACCEL;
        player2.vy += (targetVy2 - player2.vy) * PLAYER_ACCEL;
        if (input2.sprint && player2.stamina > 0) {
          player2.stamina = Math.max(0, player2.stamina - 0.15);
          player2.fatigue += 0.01;
        }
      } else {
        player2.vx *= (1 - PLAYER_DECEL);
        player2.vy *= (1 - PLAYER_DECEL);
      }
      // Power bar 2. oyuncu
      if (input2.chargingShoot) {
        state.isChargingShoot2 = true;
        state.shootPower2 = Math.min(1, (state.shootPower2 || 0) + 0.02);
      } else if (state.isChargingShoot2) {
        state.isChargingShoot2 = false;
        const d2 = dist(ball.x, ball.y, player2.x, player2.y);
        if (d2 < PLAYER_RADIUS + BALL_RADIUS + 15 && player2.kickCooldown <= 0) {
          const isPowerShot2 = Boolean(input2.sprint);
          const shotPowerBase2 = MIN_SHOOT_POWER + (MAX_SHOOT_POWER - MIN_SHOOT_POWER) * (state.shootPower2 || 0);
          const power = shotPowerBase2 * (isPowerShot2 ? 1.14 : 0.95);
          const ratingBonus = player2.rating / 500;
          const targetX = 0; // away takım sol kaleye atar
          const spread2 = isPowerShot2 ? 58 : 30;
          const targetY = state.canvasH / 2 + randomRange(-spread2, spread2) * (1 - (state.shootPower2 || 0) * 0.25);
          const dir = normalize(targetX - ball.x, targetY - ball.y);
          ball.vx = dir.x * power * (0.8 + ratingBonus);
          ball.vy = dir.y * power * (0.8 + ratingBonus);
          ball.vz = randomRange(isPowerShot2 ? 2.5 : 1.6, isPowerShot2 ? 5.5 : 3.8) * (state.shootPower2 || 0);
          ball.spin = isPowerShot2
            ? randomRange(-0.45, 0.45)
            : randomRange(-1.4, 1.4) * (0.8 + player2.rating / 220);
          ball.lastTouchedBy = player2.side;
          applyCameraShake(state, (isPowerShot2 ? 11 : 7) + Math.floor((state.shootPower2 || 0) * 6));
          player2.kickCooldown = 20;
          state.stats.away.shots++;
          const goalY = state.canvasH / 2;
          if (Math.abs(targetY - goalY) < state.goalH / 2) state.stats.away.shotsOnTarget++;
        }
        state.shootPower2 = 0;
      }
      // Stamina regen 2. oyuncu
      if (!input2.sprint) {
        const regenRate = 0.05 - player2.fatigue * 0.005;
        player2.stamina = Math.min(100, player2.stamina + Math.max(0.01, regenRate));
      }
      // 2. oyuncu switchPlayer
      if (input2.switchPlayer) {
        switchToNearestPlayerForTeam(state, 'away');
        input2.switchPlayer = false;
      }
    }
  }

  // Tüm oyuncuları güncelle
  updatePlayers(homeTeam, ball, state);
  updatePlayers(awayTeam, ball, state);

  // AI kontrol
  aiControlTeam(homeTeam, ball, state, state.controlledPlayerIndex);
  aiControlTeam(awayTeam, ball, state, state.twoPlayer ? state.controlledPlayerIndex2 : -1);

  // Aksiyonlar - 1. oyuncu
  handleActions(state, input, player, 'home');

  // Aksiyonlar - 2. oyuncu (twoPlayer modunda)
  if (state.twoPlayer && input2) {
    const player2 = awayTeam[state.controlledPlayerIndex2];
    if (player2) handleActions(state, input2, player2, 'away');
  }

  // Top fiziği
  updateBall(ball, canvasW, canvasH);

  // Top-oyuncu çarpışması
  checkBallPlayerCollisions(state);

  // Gol kontrolü
  checkGoal(state);

  // Taç & korner kontrolü - sadece PLAYING fazında ve grace süresi bitmişse
  if (state.phase === GAME_PHASE.PLAYING) {
    if (state.outOfBoundsGrace > 0) {
      state.outOfBoundsGrace--;
      // Grace süresinde topu saha içinde tut (sert duvar)
      keepBallInField(state);
    } else {
      checkOutOfBounds(state);
    }
  }

  // Top sahipliği takibi
  trackPossession(state);

  // Otomatik oyuncu değiştirme
  autoSwitchPlayer(state);

  // Momentum güncelle
  updateMomentum(state);

  return state;
}

function trackPossession(state) {
  state.possession.total++;
  if (state.ball.lastTouchedBy === 'home') {
    state.possession.home++;
  } else if (state.ball.lastTouchedBy === 'away') {
    state.possession.away++;
  }
}

function updateMomentum(state) {
  const { possession } = state;
  if (possession.total > 0) {
    state.momentum.home = Math.round((possession.home / possession.total) * 100);
    state.momentum.away = 100 - state.momentum.home;
  }
}

function updatePlayers(team, ball, state) {
  for (const p of team) {
    p.x += p.vx;
    p.y += p.vy;
    p.vx *= FRICTION;
    p.vy *= FRICTION;

    if (p.isGoalkeeper) {
      const gkMinX = p.side === 'home' ? 5 : state.canvasW - 60;
      const gkMaxX = p.side === 'home' ? 60 : state.canvasW - 5;
      p.x = clamp(p.x, gkMinX, gkMaxX);
    }

    p.x = clamp(p.x, FIELD_MARGIN * 0.5, state.canvasW - FIELD_MARGIN * 0.5);
    p.y = clamp(p.y, FIELD_MARGIN * 0.5, state.canvasH - FIELD_MARGIN * 0.5);

    if (p.kickCooldown > 0) p.kickCooldown--;
  }
}

function updateBall(ball, w, h) {
  ball.x += ball.vx;
  ball.y += ball.vy;
  ball.z += ball.vz;
  ball.vz -= GRAVITY;

  // Basit spin etkisi (havada falsolu top kıvrımı)
  if (ball.z > 0.5) {
    ball.vy += ball.spin * 0.008;
  }
  ball.spin *= 0.98;

  if (ball.z <= 0) {
    ball.z = 0;
    ball.vz = -ball.vz * 0.4;
    if (Math.abs(ball.vz) < 1) ball.vz = 0;
  }

  ball.vx *= BALL_FRICTION;
  ball.vy *= BALL_FRICTION;

  // Saha kenar duvarı - top kenara yaklaşınca yavaşlat (yumuşak duvar)
  const softMargin = FIELD_MARGIN * 0.6;
  if (ball.y < softMargin) {
    ball.vy += 0.3; // yukarı kenarda aşağı it
  }
  if (ball.y > h - softMargin) {
    ball.vy -= 0.3; // aşağı kenarda yukarı it
  }
}

// Grace süresinde topu saha sınırları içinde tut
function keepBallInField(state) {
  const { ball, canvasW, canvasH } = state;
  const margin = FIELD_MARGIN;
  if (ball.y < margin) {
    ball.y = margin;
    ball.vy = Math.abs(ball.vy) * 0.5;
  }
  if (ball.y > canvasH - margin) {
    ball.y = canvasH - margin;
    ball.vy = -Math.abs(ball.vy) * 0.5;
  }
  if (ball.x < margin) {
    ball.x = margin;
    ball.vx = Math.abs(ball.vx) * 0.5;
  }
  if (ball.x > canvasW - margin) {
    ball.x = canvasW - margin;
    ball.vx = -Math.abs(ball.vx) * 0.5;
  }
}

function checkOutOfBounds(state) {
  const { ball, canvasW, canvasH, goalY, goalH } = state;
  const goalTop = goalY;
  const goalBottom = goalY + goalH;
  const edgeMargin = FIELD_MARGIN; // kenar boşluğu
  const insideMargin = FIELD_MARGIN + BALL_RADIUS + 5; // top yerleştirme mesafesi

  // Üst kenar -> Taç atışı
  if (ball.y - BALL_RADIUS < edgeMargin - 10) {
    const throwTeam = ball.lastTouchedBy === 'home' ? 'away' : 'home';
    ball.vy = 0; ball.vx = 0; ball.vz = 0;
    ball.y = insideMargin;
    ball.x = clamp(ball.x, insideMargin, canvasW - insideMargin);

    state.phase = GAME_PHASE.THROW_IN;
    state.deadBallTimer = 45;
    state.deadBallType = 'throw_in';
    state.deadBallOwner = throwTeam;
    state.message = `TAÇ ATIŞI - ${throwTeam === 'home' ? state.homeTeamData.shortName : state.awayTeamData.shortName}`;
    state.messageTimer = 45;
    state.messageType = 'info';
    return;
  }

  // Alt kenar -> Taç atışı
  if (ball.y + BALL_RADIUS > canvasH - edgeMargin + 10) {
    const throwTeam = ball.lastTouchedBy === 'home' ? 'away' : 'home';
    ball.vy = 0; ball.vx = 0; ball.vz = 0;
    ball.y = canvasH - insideMargin;
    ball.x = clamp(ball.x, insideMargin, canvasW - insideMargin);

    state.phase = GAME_PHASE.THROW_IN;
    state.deadBallTimer = 45;
    state.deadBallType = 'throw_in';
    state.deadBallOwner = throwTeam;
    state.message = `TAÇ ATIŞI - ${throwTeam === 'home' ? state.homeTeamData.shortName : state.awayTeamData.shortName}`;
    state.messageTimer = 45;
    state.messageType = 'info';
    return;
  }

  // Sol kenar
  if (ball.x - BALL_RADIUS < edgeMargin - 10) {
    if (ball.y > goalTop && ball.y < goalBottom) return;

    ball.vx = 0; ball.vy = 0; ball.vz = 0;

    if (ball.lastTouchedBy === 'home') {
      ball.x = insideMargin + 30;
      ball.y = canvasH / 2;
      state.phase = GAME_PHASE.GOAL_KICK;
      state.deadBallTimer = 50;
      state.deadBallType = 'goal_kick';
      state.deadBallOwner = 'away';
      state.message = `KALE VURUŞU - ${state.awayTeamData.shortName}`;
    } else {
      ball.x = insideMargin;
      ball.y = ball.y < canvasH / 2 ? insideMargin : canvasH - insideMargin;
      state.phase = GAME_PHASE.CORNER_KICK;
      state.deadBallTimer = 50;
      state.deadBallType = 'corner';
      state.deadBallOwner = 'home';
      state.message = `KORNER - ${state.homeTeamData.shortName}`;
    }
    state.messageTimer = 50;
    state.messageType = 'info';
    return;
  }

  // Sağ kenar
  if (ball.x + BALL_RADIUS > canvasW - edgeMargin + 10) {
    if (ball.y > goalTop && ball.y < goalBottom) return;

    ball.vx = 0; ball.vy = 0; ball.vz = 0;

    if (ball.lastTouchedBy === 'away') {
      ball.x = canvasW - insideMargin - 30;
      ball.y = canvasH / 2;
      state.phase = GAME_PHASE.GOAL_KICK;
      state.deadBallTimer = 50;
      state.deadBallType = 'goal_kick';
      state.deadBallOwner = 'home';
      state.message = `KALE VURUŞU - ${state.homeTeamData.shortName}`;
    } else {
      ball.x = canvasW - insideMargin;
      ball.y = ball.y < canvasH / 2 ? insideMargin : canvasH - insideMargin;
      state.phase = GAME_PHASE.CORNER_KICK;
      state.deadBallTimer = 50;
      state.deadBallType = 'corner';
      state.deadBallOwner = 'away';
      state.message = `KORNER - ${state.awayTeamData.shortName}`;
    }
    state.messageTimer = 50;
    state.messageType = 'info';
  }
}

function checkGoal(state) {
  const { ball, goalY, goalH, canvasW } = state;
  const goalTop = goalY;
  const goalBottom = goalY + goalH;

  if (ball.x <= 8 && ball.y > goalTop && ball.y < goalBottom && ball.z < 30) {
    scoreGoal(state, 'away');
    return;
  }

  if (ball.x >= canvasW - 8 && ball.y > goalTop && ball.y < goalBottom && ball.z < 30) {
    scoreGoal(state, 'home');
    return;
  }
}

function scoreGoal(state, scoringTeam) {
  const { ball } = state;

  if (scoringTeam === 'home') {
    state.homeScore++;
  } else {
    state.awayScore++;
  }

  const team = scoringTeam === 'home' ? state.homeTeam : state.awayTeam;
  let scorer = team[9];
  let minDist = Infinity;
  for (const p of team) {
    if (p.isGoalkeeper) continue;
    const d = dist(p.x, p.y, ball.x, ball.y);
    if (d < minDist) {
      minDist = d;
      scorer = p;
    }
  }

  state.goalScorer = scorer;
  state.lastGoalTeam = scoringTeam;
  const teamData = scoringTeam === 'home' ? state.homeTeamData : state.awayTeamData;
  const minute = getMatchMinute(state);

  state.events.push({
    type: 'goal',
    team: scoringTeam,
    teamName: teamData.shortName,
    player: scorer.name,
    minute
  });

  state.phase = GAME_PHASE.CELEBRATION;
  state.celebrationTimer = 150;
  state.message = 'GOOOL!';
  state.messageTimer = 150;
  state.messageType = 'goal';

  ball.vx = 0; ball.vy = 0; ball.vz = 0;
}

function getMatchMinute(state) {
  const elapsed = HALF_DURATION - state.timer;
  const halfOffset = state.half === 1 ? 0 : 45;
  return Math.min(90, Math.floor((elapsed / HALF_DURATION) * 45) + halfOffset);
}

export function getMatchMinuteDisplay(state) {
  return getMatchMinute(state);
}

function checkBallPlayerCollisions(state) {
  const { ball, homeTeam, awayTeam } = state;
  const allPlayers = [...homeTeam, ...awayTeam];

  for (const p of allPlayers) {
    const d = dist(ball.x, ball.y, p.x, p.y);
    if (d < PLAYER_RADIUS + BALL_RADIUS && ball.z < 20) {
      const n = normalize(ball.x - p.x, ball.y - p.y);
      ball.x = p.x + n.x * (PLAYER_RADIUS + BALL_RADIUS + 1);
      ball.y = p.y + n.y * (PLAYER_RADIUS + BALL_RADIUS + 1);

      // Dribling: hız/rating dengesine göre ilk dokunuş kalitesi
      const playerSpeed = Math.hypot(p.vx, p.vy);
      const relativeSpeed = Math.hypot(ball.vx - p.vx, ball.vy - p.vy);
      const trapStrength = clamp(0.58 + p.rating / 320 - playerSpeed * 0.04, 0.3, 0.9);
      ball.vx = ball.vx * (1 - trapStrength) + p.vx * trapStrength;
      ball.vy = ball.vy * (1 - trapStrength) + p.vy * trapStrength;

      if (relativeSpeed > 5.8) {
        // Sert gelen topta ilk dokunuş hatası: top bir miktar açılır
        const looseTouch = (1 - trapStrength) * 1.8;
        ball.vx += n.x * looseTouch;
        ball.vy += n.y * looseTouch;
      }

      // Kontrollü sürüşte topu koşu yönünde hafif taşı
      if (relativeSpeed < 4.6 && playerSpeed > 0.6) {
        const carry = normalize(p.vx, p.vy);
        ball.x += carry.x * 1.4;
        ball.y += carry.y * 1.4;
      }

      ball.lastTouchedBy = p.side;
      p.hasBall = true;
    } else {
      p.hasBall = false;
    }
  }

  // Oyuncu-oyuncu çarpışması (fiziksel temas)
  for (let i = 0; i < allPlayers.length; i++) {
    for (let j = i + 1; j < allPlayers.length; j++) {
      const a = allPlayers[i];
      const b = allPlayers[j];
      const d = dist(a.x, a.y, b.x, b.y);
      if (d < PLAYER_RADIUS * 2 && d > 0) {
        const nx = (b.x - a.x) / d;
        const ny = (b.y - a.y) / d;
        const overlap = PLAYER_RADIUS * 2 - d;
        a.x -= nx * overlap * 0.5;
        a.y -= ny * overlap * 0.5;
        b.x += nx * overlap * 0.5;
        b.y += ny * overlap * 0.5;
        // Momentum transferi
        const push = 0.3;
        a.vx -= nx * push;
        a.vy -= ny * push;
        b.vx += nx * push;
        b.vy += ny * push;
      }
    }
  }
}

function handleActions(state, input, player, side) {
  if (!side) side = state.controlledTeam;
  const { ball } = state;
  const d = dist(ball.x, ball.y, player.x, player.y);
  const nearBall = d < PLAYER_RADIUS + BALL_RADIUS + 15;

  if (state.role === 'player' || state.twoPlayer) {
    const teamStats = side === 'home' ? state.stats.home : state.stats.away;
    // Eski şut sistemi (tek basış)
    if (input.shoot && nearBall && player.kickCooldown <= 0 && !state.isChargingShoot) {
      const isPowerShot = Boolean(input.sprint);
      const targetX = side === 'home' ? state.canvasW : 0;
      const accuracySpread = isPowerShot
        ? clamp(56 - player.rating * 0.05, 22, 56)
        : clamp(34 - player.rating * 0.07, 10, 34);
      const targetY = state.canvasH / 2 + randomRange(-accuracySpread, accuracySpread);
      const dir = normalize(targetX - ball.x, targetY - ball.y);
      const power = SHOOT_POWER * (isPowerShot ? 1.12 : 0.94) * (0.8 + player.rating / 500);
      ball.vx = dir.x * power;
      ball.vy = dir.y * power;
      ball.vz = randomRange(isPowerShot ? 2.2 : 1.4, isPowerShot ? 5.2 : 3.3);
      ball.spin = isPowerShot
        ? randomRange(-0.3, 0.3)
        : randomRange(-1.0, 1.0) * (0.5 + player.rating / 200);
      ball.lastTouchedBy = player.side;
      applyCameraShake(state, isPowerShot ? 11 : 6);
      player.kickCooldown = 20;
      teamStats.shots++;
      input.shoot = false;
    }

    if (input.pass && nearBall && player.kickCooldown <= 0) {
      const teammate = findNearestTeammateForSide(player, state, side);
      if (teammate) {
        const passError = clamp(26 - player.rating * 0.03, 6, 26);
        const dir = normalize(
          teammate.x + randomRange(-passError, passError) - ball.x,
          teammate.y + randomRange(-passError, passError) - ball.y
        );
        ball.vx = dir.x * PASS_POWER;
        ball.vy = dir.y * PASS_POWER;
        ball.vz = 1;
        ball.spin = randomRange(-0.35, 0.35);
        ball.lastTouchedBy = player.side;
        player.kickCooldown = 15;
        teamStats.passes++;
      }
      input.pass = false;
    }

    if (input.throughPass && nearBall && player.kickCooldown <= 0) {
      const forwardTeammate = findForwardTeammateForSide(player, state, side);
      if (forwardTeammate) {
        const leadX = forwardTeammate.x + forwardTeammate.vx * 12 + (side === 'home' ? 60 : -60);
        const leadY = forwardTeammate.y + forwardTeammate.vy * 8;
        const passError = clamp(30 - player.rating * 0.03, 8, 30);
        const dir = normalize(
          leadX + randomRange(-passError, passError) - ball.x,
          leadY + randomRange(-passError, passError) - ball.y
        );
        ball.vx = dir.x * THROUGH_PASS_POWER;
        ball.vy = dir.y * THROUGH_PASS_POWER;
        ball.vz = 2;
        ball.spin = randomRange(-0.25, 0.25);
        ball.lastTouchedBy = player.side;
        player.kickCooldown = 15;
        teamStats.passes++;
      }
      input.throughPass = false;
    }

    // Tackle (top rakipteyken pas tuşu) - FIFA tarzı kart sistemi
    if (input.pass && !nearBall && player.kickCooldown <= 0) {
      const oppTeam = side === 'home' ? state.awayTeam : state.homeTeam;
      for (const opp of oppTeam) {
        const dOpp = dist(player.x, player.y, opp.x, opp.y);
        if (dOpp < PLAYER_RADIUS * 3 && opp.hasBall) {
          const teamStats = side === 'home' ? state.stats.home : state.stats.away;
          teamStats.tackles++;
          const chance = 0.4 + (player.rating - opp.rating) / 200;
          if (Math.random() < chance) {
            const dir = normalize(player.x - opp.x, player.y - opp.y);
            ball.vx = dir.x * 4;
            ball.vy = dir.y * 2;
            ball.lastTouchedBy = player.side;
            player.kickCooldown = 20;
          } else {
            // Faul ve kart sistemi
            const foulChance = 0.35 + player.foulCount * 0.1;
            if (Math.random() < foulChance) {
              player.foulCount++;
              // Burada faul yapan takımın faulü
              (side === 'home' ? state.stats.home : state.stats.away).fouls++;
              
              state.message = 'FAUL!';
              state.messageTimer = 50;
              state.messageType = 'foul';
              ball.vx = 0; ball.vy = 0;
              state.deadBallTimer = 50;
              player.kickCooldown = 30;

              // Kart kontrolü
              const minute = getMatchMinute(state);
              if (player.foulCount >= 3 || (player.foulCount >= 2 && Math.random() < 0.4)) {
                if (player.yellowCards === 0) {
                  player.yellowCards = 1;
                  state.cards.push({
                    player: player.name,
                    team: player.side,
                    type: 'yellow',
                    minute
                  });
                  state.message = '🟨 SARI KART!';
                  state.messageTimer = 70;
                  state.messageType = 'card';
                  state.events.push({
                    type: 'yellow_card',
                    team: player.side,
                    teamName: player.side === 'home' ? state.homeTeamData.shortName : state.awayTeamData.shortName,
                    player: player.name,
                    minute
                  });
                } else if (player.yellowCards >= 1) {
                  player.yellowCards = 2;
                  player.redCard = true;
                  state.cards.push({
                    player: player.name,
                    team: player.side,
                    type: 'red',
                    minute
                  });
                  state.message = '🟥 KIRMIZI KART!';
                  state.messageTimer = 90;
                  state.messageType = 'card';
                  state.events.push({
                    type: 'red_card',
                    team: player.side,
                    teamName: player.side === 'home' ? state.homeTeamData.shortName : state.awayTeamData.shortName,
                    player: player.name,
                    minute
                  });
                }
              }
            }
          }
          break;
        }
      }
      input.pass = false;
    }
  } else {
    if (input.diveLeft) {
      player.vx = -2;
      player.vy = 6;
      input.diveLeft = false;
    }
    if (input.diveRight) {
      player.vx = -2;
      player.vy = -6;
      input.diveRight = false;
    }
    if (input.jumpUp) {
      player.vy = 0;
      player.vx = 2;
      if (nearBall) {
        ball.vx = 5;
        ball.vz = 8;
        ball.vy = randomRange(-3, 3);
        state.message = 'KURTARIŞ!';
        state.messageTimer = 40;
        state.messageType = 'save';
      }
      input.jumpUp = false;
    }
  }
}

function findNearestTeammate(player, state) {
  return findNearestTeammateForSide(player, state, state.controlledTeam);
}

function findNearestTeammateForSide(player, state, side) {
  const team = side === 'home' ? state.homeTeam : state.awayTeam;
  let nearest = null;
  let minDist = Infinity;
  for (const p of team) {
    if (p === player) continue;
    const d = dist(player.x, player.y, p.x, p.y);
    if (d < minDist) { minDist = d; nearest = p; }
  }
  return nearest;
}

function findForwardTeammate(player, state) {
  return findForwardTeammateForSide(player, state, state.controlledTeam);
}

function findForwardTeammateForSide(player, state, side) {
  const team = side === 'home' ? state.homeTeam : state.awayTeam;
  const forward = side === 'home' ? 1 : -1;
  let best = null;
  let bestScore = -Infinity;
  for (const p of team) {
    if (p === player || p.isGoalkeeper) continue;
    const forwardness = (p.x - player.x) * forward;
    if (forwardness > 20 && forwardness > bestScore) {
      bestScore = forwardness;
      best = p;
    }
  }
  return best || findNearestTeammateForSide(player, state, side);
}

function switchToNearestPlayerForTeam(state, side) {
  const team = side === 'home' ? state.homeTeam : state.awayTeam;
  const ball = state.ball;
  const currentIdx = side === 'home' ? state.controlledPlayerIndex : state.controlledPlayerIndex2;
  let bestIdx = currentIdx;
  let bestDist = Infinity;
  for (let i = 0; i < team.length; i++) {
    if (i === currentIdx) continue;
    if (team[i].isGoalkeeper) continue;
    const d = dist(team[i].x, team[i].y, ball.x, ball.y);
    if (d < bestDist) { bestDist = d; bestIdx = i; }
  }
  if (side === 'home') state.controlledPlayerIndex = bestIdx;
  else state.controlledPlayerIndex2 = bestIdx;
}

// AI - FIFA tarzı geliştirilmiş (tiki-taka ve pressing)
function aiControlTeam(team, ball, state, excludeIndex) {
  for (let i = 0; i < team.length; i++) {
    if (i === excludeIndex) continue;
    if (team[i].redCard) continue; // Kırmızı kartlı oyuncu oynayamaz
    aiControlPlayer(team[i], ball, state, team);
  }
}

function aiControlPlayer(player, ball, state, team) {
  const speedBase = AI_SPEED * (0.75 + player.rating / 280);
  const speed = player.isGoalkeeper ? speedBase * 0.7 : speedBase;
  const isOwnTeamBall = ball.lastTouchedBy === player.side;
  const d = dist(player.x, player.y, ball.x, ball.y);

  if (player.isGoalkeeper) {
    // Kaleci AI - FIFA tarzı geliştirilmiş
    const targetY = clamp(ball.y, state.goalY + 20, state.goalY + state.goalH - 20);
    const dy = targetY - player.y;
    player.vy = clamp(dy * 0.12, -speed * 1.2, speed * 1.2);

    // Top yaklaşırken ileri çık
    const ballComingToGoal = (player.side === 'home' && ball.vx < -2) ||
                              (player.side === 'away' && ball.vx > 2);
    if (d < 120 && ballComingToGoal) {
      const dir = normalize(ball.x - player.x, ball.y - player.y);
      player.vx += dir.x * speed * 0.8;
      player.vy += dir.y * speed * 0.8;
    }

    if (d < 80 && !isOwnTeamBall) {
      const dir = normalize(ball.x - player.x, ball.y - player.y);
      player.vx += dir.x * speed * 0.5;
      player.vy += dir.y * speed * 0.5;

      if (d < PLAYER_RADIUS + BALL_RADIUS + 10 && player.kickCooldown <= 0) {
        const clearDir = normalize(player.side === 'home' ? 1 : -1, randomRange(-1, 1));
        ball.vx = clearDir.x * 8;
        ball.vy = clearDir.y * 5;
        ball.vz = 4;
        ball.lastTouchedBy = player.side;
        player.kickCooldown = 30;
      }
    }
    return;
  }

  const nearest = isNearestToBall(player, team, ball);

  if (nearest && !isOwnTeamBall) {
    // Agresif top kazanma
    const dir = normalize(ball.x - player.x, ball.y - player.y);
    player.vx = dir.x * speed * 1.25;
    player.vy = dir.y * speed * 1.25;
  } else if (nearest && isOwnTeamBall) {
    const targetGoalX = player.side === 'home' ? state.canvasW : 0;
    const dir = normalize(targetGoalX - player.x, state.canvasH / 2 - player.y);
    player.vx = dir.x * speed * 0.8;
    player.vy = dir.y * speed * 0.5;

    const goalDist = dist(player.x, player.y, targetGoalX, state.canvasH / 2);
    if (goalDist < 200 && d < PLAYER_RADIUS + BALL_RADIUS + 10 && player.kickCooldown <= 0) {
      const shootDir = normalize(targetGoalX - ball.x, state.canvasH / 2 + randomRange(-30, 30) - ball.y);
      const power = SHOOT_POWER * (0.7 + player.rating / 400);
      ball.vx = shootDir.x * power;
      ball.vy = shootDir.y * power;
      ball.vz = randomRange(1, 4);
      ball.lastTouchedBy = player.side;
      player.kickCooldown = 30;
    } else if (d < PLAYER_RADIUS + BALL_RADIUS + 10 && player.kickCooldown <= 0 && Math.random() < 0.14) {
      // FIFA tarzı: daha sık pas, akıllı karar verme
      const teammate = findAIPassTarget(player, team, state);
      if (teammate) {
        // Önde boşluk varsa ara pas
        const forward = player.side === 'home' ? 1 : -1;
        const isForward = (teammate.x - player.x) * forward > 30;
        const passSpeed = isForward ? THROUGH_PASS_POWER : PASS_POWER;
        const leadAmount = isForward ? 20 : 5;
        const passDir = normalize(
          teammate.x + teammate.vx * leadAmount - ball.x,
          teammate.y + teammate.vy * leadAmount - ball.y
        );
        ball.vx = passDir.x * passSpeed;
        ball.vy = passDir.y * passSpeed;
        ball.vz = isForward ? 2 : 1;
        ball.lastTouchedBy = player.side;
        player.kickCooldown = 18;
      }
    }
  } else {
    // Konumlanma - FIFA tarzı akıllı pozisyon alma (geliştirilmiş)
    const offsetX = (ball.x - state.canvasW / 2) * 0.42;
    const offsetY = (ball.y - state.canvasH / 2) * 0.24;

    // Hücum/savunma dengesine göre pozisyon - daha agresif
    const attackFactor = isOwnTeamBall ? 0.2 : -0.12;
    const forward = player.side === 'home' ? 1 : -1;
    
    // Oyuncunun pozisyonuna göre farklı davranış
    const isDefender = player.position === 'CB' || player.position === 'RB' || player.position === 'LB';
    const isAttacker = player.position === 'ST' || player.position === 'LM' || player.position === 'RM';
    
    let posWeight = attackFactor;
    if (isDefender && !isOwnTeamBall) posWeight = -0.15; // Savunmacılar geri çekilsin
    if (isAttacker && isOwnTeamBall) posWeight = 0.25; // Forvetler ileri koşsun
    
    const targetX = player.homeX + offsetX + (posWeight * state.canvasW * forward);
    const targetY = player.homeY + offsetY;

    const dir = normalize(targetX - player.x, targetY - player.y);
    const distToHome = dist(player.x, player.y, targetX, targetY);

    if (distToHome > 10) {
      player.vx = dir.x * speed * 0.5;
      player.vy = dir.y * speed * 0.5;
    }
  }
}

function isNearestToBall(player, team, ball) {
  let minDist = Infinity;
  let nearest = null;
  for (const p of team) {
    if (p.isGoalkeeper) continue;
    const d = dist(p.x, p.y, ball.x, ball.y);
    if (d < minDist) { minDist = d; nearest = p; }
  }
  return nearest === player;
}

function findAIPassTarget(player, team, state) {
  const oppTeam = player.side === 'home' ? state.awayTeam : state.homeTeam;
  const forward = player.side === 'home' ? 1 : -1;
  let best = null;
  let bestScore = -Infinity;
  for (const p of team) {
    if (p === player || p.isGoalkeeper) continue;
    const forwardness = (p.x - player.x) * forward;
    const distance = dist(player.x, player.y, p.x, p.y);
    const nearestOppDist = findNearestOpponentDist(p, oppTeam);
    const pressureBonus = clamp((nearestOppDist - 40) / 80, -0.8, 1.2);
    const score = forwardness * 0.45 - distance * 0.28 + pressureBonus * 18;
    if (score > bestScore && distance > 50) { bestScore = score; best = p; }
  }
  return best;
}

function findNearestOpponentDist(player, oppTeam) {
  let minDist = Infinity;
  for (const opp of oppTeam) {
    if (opp.redCard) continue;
    const d = dist(player.x, player.y, opp.x, opp.y);
    if (d < minDist) minDist = d;
  }
  return minDist;
}

// Taraf değiştir (2. devre)
function switchSides(state) {
  const { canvasW } = state;

  for (const p of state.homeTeam) {
    p.homeX = canvasW - p.homeX;
  }
  for (const p of state.awayTeam) {
    p.homeX = canvasW - p.homeX;
  }
}

function resetPositions(state) {
  const { homeTeam, awayTeam } = state;

  state.ball.x = state.canvasW / 2;
  state.ball.y = state.canvasH / 2;
  state.ball.z = 0;
  state.ball.vx = 0;
  state.ball.vy = 0;
  state.ball.vz = 0;

  for (const p of homeTeam) {
    p.x = p.homeX; p.y = p.homeY; p.vx = 0; p.vy = 0;
  }
  for (const p of awayTeam) {
    p.x = p.homeX; p.y = p.homeY; p.vx = 0; p.vy = 0;
  }
}

function applyCameraShake(state, amount) {
  state.cameraShake = clamp((state.cameraShake || 0) + amount, 0, 14);
}
